import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchMe, fetchSubscriptions, logoutRequest } from "../api";
import { getSessionToken, clearSessionToken } from "../utils/auth";
import { buildRosteredKeys } from "../utils/rosteredNames";
import SignInForm from "./SignInForm";
import SubscriptionList from "./SubscriptionList";

// Quick-access account modal, opened when a signed-out visitor clicks a NotifyButton.
// It's the inline path that doesn't lose your place in the roster; /notifications is
// the full page version. Both share SignInForm + SubscriptionList so they can't drift.
function AccountPanel({ show, onHide, rosterData }) {
  const [me, setMe] = useState(null);
  const [subs, setSubs] = useState([]);

  const loggedIn = Boolean(getSessionToken());

  const rosteredKeys = useMemo(() => buildRosteredKeys(rosterData), [rosterData]);

  const refresh = useCallback(async () => {
    if (!getSessionToken()) {
      setMe(null);
      setSubs([]);
      return;
    }
    try {
      const [meData, subsData] = await Promise.all([fetchMe(), fetchSubscriptions()]);
      setMe(meData);
      setSubs(subsData);
    } catch {
      clearSessionToken();
      setMe(null);
      setSubs([]);
    }
  }, []);

  useEffect(() => {
    if (show) refresh();
  }, [show, refresh]);

  useEffect(() => {
    window.addEventListener("subscriptions-changed", refresh);
    return () => window.removeEventListener("subscriptions-changed", refresh);
  }, [refresh]);

  if (!show) return null;

  const handleLogout = async () => {
    try {
      await logoutRequest();
    } catch {
      // ignore - clearing the local token still logs the user out client-side
    }
    clearSessionToken();
    setMe(null);
    setSubs([]);
  };

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) onHide();
  };

  return (
    <div
      onClick={handleOutsideClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
    >
      <div
        className="card shadow"
        style={{ maxWidth: 480, width: "100%", maxHeight: "85vh", overflowY: "auto" }}
      >
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="h5 mb-0">🔔 Notifications</h2>
            <button type="button" className="btn-close" onClick={onHide} aria-label="Close" />
          </div>

          {!loggedIn ? (
            <SignInForm blurb="Sign in with your email to get notified when a performer you follow is on tomorrow's roster." />
          ) : (
            <>
              <p className="mb-2">
                <strong>Signed in as</strong> {me?.email}
              </p>

              <div className="mb-3">
                <strong className="d-block mb-1">Telegram alerts</strong>
                {me?.telegram_linked ? (
                  <span className="text-success small">
                    ✅ Linked - you'll get Telegram messages too.
                  </span>
                ) : (
                  <div className="small text-muted">
                    Message{" "}
                    {me?.telegram_bot_username ? `@${me.telegram_bot_username}` : "the bot"} on
                    Telegram with:
                    <br />
                    <code>/start {me?.telegram_link_code}</code>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <strong className="d-block mb-1">Following ({subs.length})</strong>
                <SubscriptionList
                  subscriptions={subs}
                  onChanged={refresh}
                  rosteredKeys={rosteredKeys}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <Link to="/notifications" className="btn btn-sm btn-outline-primary" onClick={onHide}>
                  Manage all notifications →
                </Link>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleLogout}
                >
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountPanel;
